import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector, RootState } from '../store';
import { useAuth } from './useAuth';

import {
  fetchConversations,
  createConversation,
  fetchMessages,
  markAllAsRead,
  markMessageAsRead,
  setActiveConversation,
  clearError,
} from '../store/chat/chatSlice';

import {
  CreateConversationRequest,
  SendMessageRequest,
  MessagesListParams,
} from '../types/chat.types';
import { chatService } from '../services/chat.service';

export interface ChatController {
  conversations: RootState['chat']['conversations'];
  activeConversation: RootState['chat']['conversations'][0] | undefined;
  activeConversationId: number | null;
  activeMessages: RootState['chat']['messages'][number];
  loadingConversations: boolean;
  loadingMessages: boolean;
  sendingMessage: boolean;
  error: string | null;
  totalUnreadCount: number;
  canLoadMoreMessages: boolean;
  currentMessagesPage: number;
  isReady: boolean;
  isConnected: boolean;
  typingUsers: string[];
  loadConversations: () => Promise<any>;
  startConversation: (data: CreateConversationRequest) => Promise<any>;
  loadMessages: (conversationId: number, params?: MessagesListParams) => Promise<any>;
  loadMoreMessages: (conversationId: number) => Promise<any>;
  sendNewMessage: (conversationId: number, data: SendMessageRequest) => void;
  handleTyping: () => void;
  markConversationAsRead: (conversationId: number) => Promise<any>;
  markSingleMessageAsRead: (messageId: string) => Promise<any>;
  setCurrentConversation: (conversationId: number | null) => void;
  clearChatError: () => void;
  openConversationWithUser: (targetUserId: number) => Promise<number | null>;
  formatMessageTime: (timestamp: string) => string;
  truncateMessage: (message: string, maxLength?: number) => string;
}

export const useChat = (): ChatController => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const conversationsLoadedRef = useRef(false);
  
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingSentRef = useRef(false);

  const {
    conversations,
    activeConversationId,
    messages,
    loadingConversations,
    loadingMessages,
    sendingMessage,
    error,
    messagesPage,
    hasMoreMessages,
    isConnected,
    typingUsers,
  } = useAppSelector((state) => state.chat);

  const activeConversation = useMemo(() => 
    conversations.find((conv) => conv.id === activeConversationId),
    [conversations, activeConversationId]
  );

  const activeMessages = useMemo(() => 
    activeConversationId ? messages[activeConversationId] || [] : [],
    [activeConversationId, messages]
  );

  const canLoadMoreMessages = activeConversationId
    ? hasMoreMessages[activeConversationId] ?? true
    : false;

  const currentMessagesPage = activeConversationId
    ? messagesPage[activeConversationId] || 1
    : 1;

  const totalUnreadCount = useMemo(() => {
    if (!isAuthenticated || !user) {
      return 0;
    }
    return conversations.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0);
  }, [conversations, isAuthenticated, user]);

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    conversationsLoadedRef.current = true;
    return dispatch(fetchConversations());
  }, [dispatch, isAuthenticated, user]);

  const startConversation = useCallback(
    async (data: CreateConversationRequest) => {
      if (!isAuthenticated || !user) throw new Error('User must be authenticated');
      return dispatch(createConversation(data));
    },
    [dispatch, isAuthenticated, user]
  );

  const loadMessages = useCallback(
    async (conversationId: number, params?: MessagesListParams) => {
      if (!isAuthenticated || !user) return;
      return dispatch(fetchMessages({ conversationId, params }));
    },
    [dispatch, isAuthenticated, user]
  );

  const loadMoreMessages = useCallback(
    async (conversationId: number) => {
      if (!isAuthenticated || !user) return;
      const nextPage = (messagesPage[conversationId] || 1) + 1;
      return dispatch(fetchMessages({
        conversationId,
        params: { page: nextPage, limit: 50 }
      }));
    },
    [dispatch, messagesPage, isAuthenticated, user]
  );

  const sendNewMessage = useCallback(
    (conversationId: number, data: SendMessageRequest) => {
      if (!isAuthenticated || !user || !isConnected) {
        return;
      }
      chatService.sendRealtimeMessage(conversationId, data.message, data.fileId);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (isTypingSentRef.current) {
        chatService.sendTypingStop(conversationId);
        isTypingSentRef.current = false;
      }
    },
    [isAuthenticated, user, isConnected]
  );

  const handleTyping = useCallback(() => {
    if (!activeConversationId || !isConnected) return;

    if (!isTypingSentRef.current) {
      chatService.sendTypingStart(activeConversationId);
      isTypingSentRef.current = true;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      chatService.sendTypingStop(activeConversationId);
      isTypingSentRef.current = false;
    }, 3000);
  }, [activeConversationId, isConnected]);

  const markConversationAsRead = useCallback(
    async (conversationId: number) => {
      if (!isAuthenticated || !user) return;
      return dispatch(markAllAsRead(conversationId));
    },
    [dispatch, isAuthenticated, user]
  );

  const markSingleMessageAsRead = useCallback(
    async (messageId: string) => {
      if (!isAuthenticated || !user) return;
      return dispatch(markMessageAsRead(messageId));
    },
    [dispatch, isAuthenticated, user]
  );

  const setCurrentConversation = useCallback(
    (conversationId: number | null) => {
      dispatch(setActiveConversation(conversationId));
    },
    [dispatch]
  );

  const clearChatError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const openConversationWithUser = useCallback(
    async (targetUserId: number) => {
      if (!isAuthenticated || !user) throw new Error('User must be authenticated');
      try {
        const existingConversation = conversations.find(
          (conv) => conv.participantId === targetUserId
        );
        if (existingConversation) {
          setCurrentConversation(existingConversation.id);
          return existingConversation.id;
        }
        const result = await dispatch(createConversation({ targetUserId }));
        if (createConversation.fulfilled.match(result)) {
          const conversationId = result.payload.id;
          await loadConversations();
          setCurrentConversation(conversationId);
          return conversationId;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    [conversations, dispatch, setCurrentConversation, loadConversations, isAuthenticated, user]
  );

  useEffect(() => {
    if (isAuthenticated && user && !conversationsLoadedRef.current && !loadingConversations) {
      loadConversations();
    }
  }, [isAuthenticated, user, loadingConversations, loadConversations]);

  useEffect(() => {
    if (isAuthenticated && user && activeConversationId && !messages[activeConversationId] && !loadingMessages) {
      loadMessages(activeConversationId);
    }
  }, [activeConversationId, isAuthenticated, user, loadingMessages, messages, loadMessages]);

  useEffect(() => {
    if (isAuthenticated && user && activeConversation && activeConversation.unreadCount !== undefined && activeConversation.unreadCount > 0) {
      markConversationAsRead(activeConversation.id);
    }
  }, [activeConversation, isAuthenticated, user, markConversationAsRead]);
  
  const formatMessageTime = useCallback(
    (timestamp: string) => chatService.formatMessageTime(timestamp),
    []
  );

  const truncateMessage = useCallback(
    (message: string, maxLength?: number) =>
      chatService.truncateMessage(message, maxLength),
    []
  );

  return useMemo(() => ({
    conversations,
    activeConversation,
    activeConversationId,
    activeMessages,
    loadingConversations,
    loadingMessages,
    sendingMessage,
    error,
    totalUnreadCount,
    canLoadMoreMessages,
    currentMessagesPage,
    isReady: !!(isAuthenticated && user),
    isConnected,
    typingUsers: activeConversationId ? (typingUsers[activeConversationId] || []) : [],
    loadConversations,
    startConversation,
    loadMessages,
    loadMoreMessages,
    sendNewMessage,
    handleTyping,
    markConversationAsRead,
    markSingleMessageAsRead,
    setCurrentConversation,
    clearChatError,
    openConversationWithUser,
    formatMessageTime,
    truncateMessage,
  }), [
    conversations, activeConversation, activeConversationId, activeMessages,
    loadingConversations, loadingMessages, sendingMessage, error,
    canLoadMoreMessages, currentMessagesPage, isAuthenticated, user, isConnected,
    typingUsers,
    loadConversations, startConversation, loadMessages, loadMoreMessages,
    sendNewMessage, handleTyping, markConversationAsRead, markSingleMessageAsRead,
    setCurrentConversation, clearChatError, openConversationWithUser,
    formatMessageTime, truncateMessage
  ]);
};
